import BaseService from './BaseService'
import { fixImageOrientation } from '@/utils/imageOrientation'

class CustomerService extends BaseService {
  constructor() {
    super('/customers')
  }

  async list(params) {
    // Avoid trailing-slash redirects (can become mixed-content behind proxies)
    return this.customRequest('get', '', null, { params })
  }

  async listArchived(params = {}) {
    return this.customRequest('get', '/archived', null, { params })
  }

  async restore(id) {
    return this.customRequest('post', `/${id}/restore`)
  }

  async createCustomer(customerData, imageFile = null) {
    if (!(imageFile instanceof File)) {
      return this.create(customerData)
    }
    const fixedFile = await fixImageOrientation(imageFile)
    const formData = new FormData()
    formData.append('customer', JSON.stringify(customerData))
    formData.append('image', fixedFile)
    return this.customRequest('post', '', formData)
  }

  async getCustomerEmails(customerId) {
    return this.customRequest('get', `/${customerId}/emails`)
  }

  async addCustomerEmail(customerId, emailData) {
    return this.customRequest('post', `/${customerId}/emails`, emailData)
  }

  async updateCustomerEmail(customerId, emailId, emailData) {
    return this.customRequest('put', `/${customerId}/emails/${emailId}`, emailData)
  }

  async deleteCustomerEmail(customerId, emailId) {
    return this.customRequest('delete', `/${customerId}/emails/${emailId}`)
  }

  async getCustomerPhones(customerId) {
    return this.customRequest('get', `/${customerId}/phones`)
  }

  async addCustomerPhone(customerId, phoneData) {
    return this.customRequest('post', `/${customerId}/phones`, phoneData)
  }

  async updateCustomerPhone(customerId, phoneId, phoneData) {
    return this.customRequest('put', `/${customerId}/phones/${phoneId}`, phoneData)
  }

  async deleteCustomerPhone(customerId, phoneId) {
    return this.customRequest('delete', `/${customerId}/phones/${phoneId}`)
  }

  async getInsights(customerId, params = {}) {
    return this.customRequest('get', `/${customerId}/insights`, null, { params })
  }

  async getLoyaltyTransactions(customerId) {
    return this.customRequest('get', `/${customerId}/loyalty-transactions`)
  }

  async getRedeemableProducts(customerId) {
    return this.customRequest('get', `/${customerId}/loyalty/redeemable-products`)
  }

  async redeemProduct(customerId, productId, quantity, notes) {
    return this.customRequest('post', `/${customerId}/loyalty/redeem-product`, {
      product_id: productId,
      quantity,
      notes
    })
  }

  async redeemProductForSale(customerId, productId, quantity, notes, saleId, lineItemId) {
    return this.customRequest('post', `/${customerId}/loyalty/redeem-product-for-sale`, {
      product_id: productId,
      quantity,
      notes,
      sale_id: saleId,
      line_item_id: lineItemId
    })
  }

  async getRedemptionHistory(customerId) {
    return this.customRequest('get', `/${customerId}/loyalty/redemption-history`)
  }

  async uploadPhoto(customerId, imageFile) {
    if (!(imageFile instanceof File)) {
      throw new Error('Image must be a File object')
    }
    const fixedFile = await fixImageOrientation(imageFile)
    const formData = new FormData()
    formData.append('image', fixedFile)
    return this.customRequest('post', `/${customerId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }

  async deletePhoto(customerId) {
    return this.customRequest('delete', `/${customerId}/photo`)
  }

  async mergeCustomer(targetCustomerId, sourceCustomerId) {
    return this.customRequest('post', `/${targetCustomerId}/merge`, {
      source_customer_id: sourceCustomerId
    })
  }

  async sendPortalSignInLink(customerId, { confirmFirstInvite = false } = {}) {
    return this.customRequest('post', `/${customerId}/portal/sign-in-link`, {
      confirm_first_invite: confirmFirstInvite
    })
  }
}

export const customerService = new CustomerService()
